import React, { useCallback } from 'react';
import { combineClasses } from '../../../utils/formatters';
import { TOPBAR_CONFIG } from '../../../config/topbarConfig';
import { LocationIcon, ChevronDownIcon } from '../../ui/SvgIcons';

// Location Dropdown Component - Optimized
export const LocationDropdown = React.memo(({ 
  showLocationDropdown, 
  locations, 
  handleLocationSelect 
}) => {
  const handleLocationClick = useCallback((location) => {
    handleLocationSelect(location);
  }, [handleLocationSelect]);

  if (!showLocationDropdown) return null;

  return (
    <div 
      className={combineClasses(
        TOPBAR_CONFIG.COMPONENTS.dropdown.container,
        TOPBAR_CONFIG.LOCATION.dropdownWidth,
        `border border-${TOPBAR_CONFIG.COLORS.border}`
      )}
      role="menu"
      aria-label="Location selection"
    >
      <div className={combineClasses(
        "px-3 py-2",
        TOPBAR_CONFIG.COMPONENTS.typography.textXs,
        TOPBAR_CONFIG.COMPONENTS.typography.fontMedium,
        TOPBAR_CONFIG.COMPONENTS.misc.uppercase,
        TOPBAR_CONFIG.COMPONENTS.misc.trackingWide,
        `text-${TOPBAR_CONFIG.COLORS.primary}`
      )}>
        {TOPBAR_CONFIG.LOCATION.sectionTitle}
      </div>
      {locations.map((location, index) => (
        <button
          key={index}
          onClick={() => handleLocationClick(location)}
          className={combineClasses(
            "w-full",
            TOPBAR_CONFIG.COMPONENTS.alignment.textLeft,
            "px-3 py-2",
            TOPBAR_CONFIG.COMPONENTS.typography.textSm,
            `text-${TOPBAR_CONFIG.COLORS.text}`,
            TOPBAR_CONFIG.HOVER_EFFECTS.dropdown,
            TOPBAR_CONFIG.TRANSITIONS.default
          )}
          role="menuitem"
          tabIndex={-1}
        >
          {location}
        </button>
      ))}
    </div>
  );
});

LocationDropdown.displayName = 'LocationDropdown';

// Location Selector Component - Optimized with accessibility
export const TopBarLocation = React.memo(({ 
  showLocation, 
  currentLocation, 
  showLocationDropdown, 
  setShowLocationDropdown, 
  locations, 
  handleLocationSelect,
  locationDropdownRef 
}) => {
  const handleToggleDropdown = useCallback(() => {
    setShowLocationDropdown(prev => !prev);
  }, [setShowLocationDropdown]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDropdown();
    } else if (event.key === 'Escape') {
      setShowLocationDropdown(false);
    }
  }, [handleToggleDropdown, setShowLocationDropdown]);

  if (!showLocation) return null;

  return (
    <div 
      className={combineClasses(
        TOPBAR_CONFIG.COMPONENTS.alignment.itemsCenter, 
        TOPBAR_CONFIG.COMPONENTS.spacing.marginRight2,
        "mr-2", // Additional margin for proper spacing
        TOPBAR_CONFIG.LOCATION.visibility
      )} 
      ref={locationDropdownRef}
    >
      <div className={TOPBAR_CONFIG.COMPONENTS.display.relative}>
        <button
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyDown}
          className={combineClasses(
            TOPBAR_CONFIG.COMPONENTS.button.location,
            `text-${TOPBAR_CONFIG.COLORS.text}`,
            TOPBAR_CONFIG.HOVER_EFFECTS.location,
            TOPBAR_CONFIG.TRANSITIONS.default
          )}
          aria-label={`Current location: ${currentLocation}`}
          aria-expanded={showLocationDropdown}
          aria-haspopup="menu"
        >
          <LocationIcon className={combineClasses(
            TOPBAR_CONFIG.COMPONENTS.spacing.marginRight2, 
            "text-gray-500"
          )} />
          <span className={combineClasses(
            TOPBAR_CONFIG.LOCATION.maxWidth, 
            TOPBAR_CONFIG.COMPONENTS.misc.truncate
          )}>
            {currentLocation}
          </span>
          <ChevronDownIcon className={combineClasses(
            TOPBAR_CONFIG.COMPONENTS.spacing.marginLeft1, 
            "text-gray-400"
          )} />
        </button>

        <LocationDropdown
          showLocationDropdown={showLocationDropdown}
          locations={locations}
          handleLocationSelect={handleLocationSelect}
        />
      </div>
    </div>
  );
});

TopBarLocation.displayName = 'TopBarLocation';
